import type { AxiosError } from "axios";
import { logError, safeStringify } from "@/utils/errorHandler";

export type ApiProblemDetail = {
  detail?: string;
  message?: string;
  error_description?: string;
  /** RFC 7807 `title` — a stable machine-readable error code (e.g.
   * "REVIEW_ALREADY_RESOLVED"), distinct from `detail`'s human-readable text.
   * Needed wherever the same HTTP status covers more than one meaning (e.g.
   * GPRT's decision endpoint returns 409 for both "already resolved by a
   * concurrent decision" and "not priced yet" — status alone can't tell
   * those apart, only `title` can). */
  title?: string;
};

/**
 * Shared by gpcApi's, GPRT's gprtApi's, and Active Leads' portalApi response
 * interceptors — same RFC 7807 ProblemDetail error shape across all three
 * sarah-service surfaces (see gpc-gprt-api-contract.md's Common Error
 * Envelope), so this is the one place that needs updating if the shape ever
 * changes, instead of copies drifting (GPRT's had already lost the
 * `error_description` fallback, and Active Leads' had too, before both were
 * factored out to this one implementation).
 *
 * `onError` is an optional hook fired with the raw error (before the 401/
 * message rewrite below) — e.g. Active Leads' PostHog tracking, which reads
 * `error.config`/`error.response` in their original form.
 */
export const createResponseErrorInterceptor =
  (
    logPrefix: string,
    onError?: (error: AxiosError<ApiProblemDetail>) => void
  ) =>
  (error: AxiosError<ApiProblemDetail>) => {
    logError(
      error,
      `${logPrefix}: ${error.config?.method?.toUpperCase() || "API"} ${
        error.config?.url || "unknown"
      }`
    );

    onError?.(error);

    if (error.response?.status === 401) {
      error.message = "Authentication failed. Please check your login session.";
      (error as AxiosError & { isAuthError?: boolean }).isAuthError = true;
      return Promise.reject(error);
    }

    // `detail` is the actual field sarah-service sends; `message`/
    // `error_description` don't exist on these responses, so checking them
    // first meant every error here silently fell back to Axios's generic
    // "Request failed with status code NNN" instead of the real backend text.
    const rawMessage =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.response?.data?.error_description ||
      error.message ||
      "An unexpected error occurred";

    error.message = safeStringify(rawMessage);
    error.status = error.response?.status;
    (error as AxiosError & { title?: string }).title =
      error.response?.data?.title;
    return Promise.reject(error);
  };
